"use client";

import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  HouseIcon,
  HammerBuildIcon,
  LeafIcon,
  ShieldIcon,
  HandshakeIcon,
  BulbIcon,
  CompassIcon,
  PenIcon,
  KeyIcon,
  PhoneIcon,
  MailIcon,
  PinIcon,
  MenuIcon,
  CloseIcon,
  CheckIcon,
  FacebookIcon,
  InstagramIcon,
  YoutubeIcon,
} from "@/components/icons";

const NAV = [
  { href: "#sluzby", label: "Služby" },
  { href: "#postup", label: "Jak postupujeme" },
  { href: "#realizace", label: "Realizace" },
  { href: "#o-nas", label: "O nás" },
  { href: "#kontakt", label: "Kontakt" },
];

const HERO_IMG =
  "https://sfile.chatglm.cn/images-ppt/87896ca24756.png";

const STORY_IMG =
  "https://sfile.chatglm.cn/images-ppt/976a188abe32.jpeg";

const PORTFOLIO_IMAGES = [
  "https://sfile.chatglm.cn/images-ppt/b50bb8560d37.jpg",
  "https://sfile.chatglm.cn/images-ppt/3689147f7119.jpg",
  "https://sfile.chatglm.cn/images-ppt/2f2a40ed7e93.jpg",
  "https://sfile.chatglm.cn/images-ppt/ec17b4919adc.png",
  "https://sfile.chatglm.cn/images-ppt/86e15cdd455e.jpg",
  "https://sfile.chatglm.cn/images-ppt/447fd849bccc.jpg",
];

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <Header scrolled={scrolled} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <main>
        <Hero />
        <Marquee />
        <Services />
        <Story />
        <Process />
        <Portfolio />
        <Values />
        <Testimonial />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Header                                                              */
/* ------------------------------------------------------------------ */
function Header({
  scrolled,
  menuOpen,
  setMenuOpen,
}: {
  scrolled: boolean;
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
}) {
  return (
    <header className={`site-header ${scrolled ? "scrolled" : ""}`}>
      <div className="container header-inner">
        <a href="#top" className="brand" aria-label="TinyHouse Volha — domů">
          <span className="brand-mark">
            <img src="/brand/logo.jpg" alt="TinyHouse Volha" />
          </span>
          <span className="brand-text">
            <span className="brand-name">TinyHouse Volha</span>
            <span className="brand-sub">Výroba &amp; stavba</span>
          </span>
        </a>

        <nav className="nav" aria-label="Hlavní navigace">
          {NAV.map((n) => (
            <a key={n.href} href={n.href}>
              {n.label}
            </a>
          ))}
        </nav>

        <div className="header-cta">
          <a href="tel:+420774287783" className="phone">
            +420 774 287 783
          </a>
          <a href="#kontakt" className="btn btn-primary btn-arrow">
            Nezávazná poptávka
            <ArrowRight />
          </a>
        </div>

        <button
          className="mobile-toggle"
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>

      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
        {NAV.map((n) => (
          <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)}>
            {n.label}
          </a>
        ))}
        <a
          href="#kontakt"
          className="btn btn-primary btn-arrow"
          onClick={() => setMenuOpen(false)}
        >
          Nezávazná poptávka <ArrowRight />
        </a>
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */
function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero-img">
        <img src={HERO_IMG} alt="Tiny house v přírodě při západu slunce" />
      </div>
      <div className="container hero-content">
        <span className="hero-eyebrow">TinyHouse Volha — od roku 2018</span>
        <h1>
          Stavíme domy, <em>ve kterých se dobře žije</em>
        </h1>
        <p className="hero-sub">
          Specializujeme se na výrobu a stavbu Tiny House domů na míru.
          Kromě toho nabízíme široké spektrum stavebních prací — od menších
          úprav až po kompletní realizace po celé České republice.
        </p>
        <div className="hero-actions">
          <a href="#kontakt" className="btn btn-primary btn-arrow">
            Nezávazná poptávka <ArrowRight />
          </a>
          <a href="#realizace" className="btn btn-light btn-arrow">
            Naše realizace <ArrowRight />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Marquee                                                             */
/* ------------------------------------------------------------------ */
function Marquee() {
  const items = [
    "Tiny House na míru",
    "Hrubé stavby",
    "Kompletní rekonstrukce",
    "Bourací práce",
    "Fasády a omítky",
    "Sádrokartonářské práce",
    "Obklady a dlažby",
    "Podlahy — laminát, vinyl, dřevo",
    "Zámková dlažba",
  ];
  const doubled = [...items, ...items];
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-track">
        {doubled.map((it, i) => (
          <span key={i}>{it}</span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Services                                                            */
/* ------------------------------------------------------------------ */
function Services() {
  return (
    <section className="section" id="sluzby">
      <div className="container">
        <div className="section-head-row">
          <div className="left">
            <span className="eyebrow">Co pro vás uděláme</span>
            <h2 style={{ marginTop: "1rem" }}>
              Od domku na kolech po kompletní rekonstrukci
            </h2>
          </div>
          <div className="right">
            <a href="#kontakt" className="btn btn-ghost btn-arrow">
              Domluvit schůzku <ArrowRight />
            </a>
          </div>
        </div>

        <div className="services-grid">
          {/* Service 1 — featured */}
          <article className="service-card featured">
            <div className="service-num">01 / Naše specializace</div>
            <HouseIcon className="service-icon" />
            <h3>Tiny House na míru</h3>
            <p className="muted">
              Navrhujeme a stavíme Tiny House přesně podle vašich představ.
              Klade­me důraz na kvalitní materiály, promyšlené řešení
              prostoru a dlouhou životnost.
            </p>
            <ul className="service-list">
              <li>Individuální architektonický návrh</li>
              <li>Mobilní i stacionární varianty</li>
              <li> kompletní vybavení včetně instalací</li>
              <li>Dodání a posazení na místo</li>
            </ul>
            <a href="#kontakt" className="service-link">
              Domluvit konzultaci <ArrowRight />
            </a>
          </article>

          {/* Service 2 */}
          <article className="service-card">
            <div className="service-num">02 / Stavební práce</div>
            <HammerBuildIcon className="service-icon" />
            <h3>Stavební práce</h3>
            <p className="muted">
              Od hrubé stavby po kompletní rekonstrukci. Pracujeme kvalitně,
              spolehlivě a v dohodnutém termínu. Bourání, zedničina, fasády,
              podlahy — vše pod jednou střechou.
            </p>
            <ul className="service-list">
              <li>Hrubé stavby a kompletní rekonstrukce domů i bytů</li>
              <li>Bourací práce</li>
              <li>Fasády a omítky</li>
              <li>Sádrokartonářské práce</li>
              <li>Obklady a dlažby</li>
              <li>Laminátové, vinylové a dřevěné podlahy</li>
              <li>Pokládka zámkové dlažby</li>
            </ul>
            <a href="#kontakt" className="service-link">
              Poptat stavební práce <ArrowRight />
            </a>
          </article>

          {/* Service 3 */}
          <article className="service-card">
            <div className="service-num">03 / Náš přístup</div>
            <LeafIcon className="service-icon" />
            <h3>Individuální přístup</h3>
            <p className="muted">
              Každý projekt řešíme samostatně, podle požadavků zákazníka
              a konkrétního prostředí. Vše probereme, navrhneme a pak teprve
              postavíme — žádné šablony.
            </p>
            <ul className="service-list">
              <li>Osobní konzultace zdarma</li>
              <li>3D vizualizace projektu</li>
              <li>Průběžná komunikace během stavby</li>
              <li>Záruka a servis po předání</li>
            </ul>
            <a href="#kontakt" className="service-link">
              Domluvit schůzku <ArrowRight />
            </a>
          </article>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Story / About                                                       */
/* ------------------------------------------------------------------ */
function Story() {
  return (
    <section className="section story" id="o-nas">
      <div className="container">
        <div className="story-grid">
          <div className="story-img">
            <img
              src={STORY_IMG}
              alt="Interiér tiny house ve výrobě — ruční práce se dřevem"
            />
            <span className="badge">Volha — Mladá Boleslav</span>
          </div>

          <div className="story-text">
            <span className="eyebrow">O nás</span>
            <h2 style={{ marginTop: "1rem" }}>
              Řemeslo, které má na starosti rodina a pár poctivých tesařů
            </h2>
            <p>
              TinyHouse Volha vznikla v Mladé Boleslavi z touhy postavit
              si dům po svém — menší, chytřejší, blíž k přírodě. Co začalo
              jako jeden domek na kolech pro vlastní rodinu, je dnes malá
              firma s týmem, který postavil přes šedesát realizací po celé
              České republice.
            </p>
            <p>
              Stavíme Tiny House i běžné stavební zakázky. Vždy stejným
              způsobem: nejdřív si vyslechneme, co potřebujete, pak navrhneme
              řešení, které dává smysl — a teprve pak sáhneme po nářadí.
              Žádné předepsané balíčky, žádné překvapení ve fakturaci.
            </p>
            <p>
              Dřevo vybíráme od českých pil, kovové prvky si často vyrábíme
              sami, a každou realizaci předáme až tehdy, když bychom si v ní
              sami bez problémů bydleli.
            </p>
            <div className="signature">
              <div>
                <div className="name">Volha &amp; tým</div>
                <div className="role">Stavitelé &amp; řemeslníci</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Process                                                             */
/* ------------------------------------------------------------------ */
function Process() {
  const steps = [
    {
      num: "01",
      icon: <PhoneIcon className="process-icon" />,
      title: "První kontakt",
      text: "Nazvete nebo vyplníte poptávku. Do dvou dnů se ozveme a probereme vaši představu.",
    },
    {
      num: "02",
      icon: <PenIcon className="process-icon" />,
      title: "Návrh a vizualizace",
      text: "Navrhneme řešení na míru, představíme 3D vizualizaci a rozpočtujeme cenu. Zdarma.",
    },
    {
      num: "03",
      icon: <HammerBuildIcon className="process-icon" />,
      title: "Výroba &amp; stavba",
      text: "Postavíme v dílně i na místě. Průběžně vás informujeme o postupu a termínech.",
    },
    {
      num: "04",
      icon: <KeyIcon className="process-icon" />,
      title: "Předání klíčů",
      text: "Předáme hotový projekt, vše vysvětlíme a zůstáváme k dispozici se servisem.",
    },
  ];
  return (
    <section className="section process" id="postup">
      <div className="container">
        <div className="section-head center">
          <span className="eyebrow">Jak postupujeme</span>
          <h2>Čtyři kroky od nápadu k předání</h2>
          <p className="lede">
            Průběh je vždy stejný — transparentní a v dohodnutém čase.
            Žádná překvapení, žádné skryté položky.
          </p>
        </div>
        <div className="process-list">
          {steps.map((s) => (
            <div className="process-step" key={s.num}>
              <div className="process-step-top">
                <span className="step-num">{s.num}</span>
                <span className="step-line" aria-hidden="true" />
              </div>
              {s.icon}
              <h3 className="process-step-title" dangerouslySetInnerHTML={{ __html: s.title }} />
              <p dangerouslySetInnerHTML={{ __html: s.text }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Portfolio                                                           */
/* ------------------------------------------------------------------ */
function Portfolio() {
  const items = [
    {
      img: PORTFOLIO_IMAGES[0],
      cat: "Tiny House",
      title: "Domek Podlesí — 24 m²",
      tall: true,
    },
    {
      img: PORTFOLIO_IMAGES[1],
      cat: "Interiér",
      title: "Loftová ložnice",
    },
    {
      img: PORTFOLIO_IMAGES[2],
      cat: "Exteriér",
      title: "Dům u jezera",
    },
    {
      img: PORTFOLIO_IMAGES[3],
      cat: "Stavební práce",
      title: "Rekonstrukce v Praze",
    },
    {
      img: PORTFOLIO_IMAGES[4],
      cat: "Tiny House",
      title: "Mobilní dům Volha",
      tall: true,
    },
    {
      img: PORTFOLIO_IMAGES[5],
      cat: "Interiér",
      title: "Kuchyně z masivu",
    },
  ];

  return (
    <section className="section portfolio" id="realizace">
      <div className="container">
        <div className="section-head-row">
          <div className="left">
            <span className="eyebrow">Naše realizace</span>
            <h2 style={{ marginTop: "1rem" }}>Co jsme postavili</h2>
            <p className="lede" style={{ marginTop: "1rem" }}>
              Výběr z projektů z let 2021–2025. Každý dům i každou rekonstrukci
              stavěl tým Volha od návrhu až po předání klíčů.
            </p>
          </div>
          <div className="right">
            <a href="#kontakt" className="btn btn-light btn-arrow">
              Zobrazit vše <ArrowRight />
            </a>
          </div>
        </div>

        <div className="portfolio-grid">
          {items.map((it, i) => (
            <article
              className={`portfolio-item ${it.tall ? "tall" : ""}`}
              key={i}
            >
              <img src={it.img} alt={`${it.cat} — ${it.title}`} />
              <span className="p-arrow">
                <ArrowUpRight />
              </span>
              <div className="portfolio-meta">
                <div className="p-cat">{it.cat}</div>
                <div className="p-title">{it.title}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Values                                                              */
/* ------------------------------------------------------------------ */
function Values() {
  const values = [
    {
      icon: <ShieldIcon className="v-icon" />,
      title: "Kvalitní řemeslné zpracování",
      text: "Pracujeme s českými materiály a ručně osvědčeným nářadím. Žádné zkratky, žádný laminát, který má za rok odštěpené hrany.",
    },
    {
      icon: <BulbIcon className="v-icon" />,
      title: "Individuální řešení",
      text: "Nenabízíme hotové balíčky. Každý dům i rekonstrukce vzniká v dialogu s vámi a podle podmínek konkrétního místa.",
    },
    {
      icon: <HandshakeIcon className="v-icon" />,
      title: "Spolehlivost a férové jednání",
      text: "Co si dohodneme, to dodržíme — v ceně i v termínu. Stavíme vztahy, ne jen domy. Polovina klientů k nám přichází na doporučení.",
    },
    {
      icon: <LeafIcon className="v-icon" />,
      title: "Moderní a praktické bydlení",
      text: "Málo místa neznamená málo pohodlí. Promyšlené dispozice, úložné prostory a materiály, které stárnou hezky.",
    },
  ];
  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Proč TinyHouse Volha</span>
          <h2>Čtyři věci, na kterých si zakládáme</h2>
        </div>
        <div className="values-grid">
          {values.map((v, i) => (
            <div className="value-card" key={i}>
              {v.icon}
              <h3>{v.title}</h3>
              <p>{v.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Testimonial                                                         */
/* ------------------------------------------------------------------ */
function Testimonial() {
  return (
    <section className="section testimonial">
      <div className="container">
        <div className="testimonial-inner">
          <span className="quote-mark">&ldquo;</span>
          <blockquote>
            Chtěli jsme víkendový domek, kde se nebudeme tłčit, ale zároveň
            se vejde do přívěsu. Kluci z Volha navrhli dispozici na milimetr,
            <em> postavili za dva měsíce</em> a předali přesně tak, jak jsme
            si představovali. Dva roky v něm jezdíme po celé Evropě.
          </blockquote>
          <div className="author">
            <div>
              <div className="a-name">Markéta &amp; Tomáš Jelínek</div>
              <div className="a-meta">Tiny House „Podmrak", 2023</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Contact + Form                                                      */
/* ------------------------------------------------------------------ */
function Contact() {
  return (
    <section className="section cta" id="kontakt">
      <div className="container">
        <div className="cta-grid">
          <div className="cta-left">
            <span className="eyebrow">Máte představu o svém projektu?</span>
            <h2 style={{ marginTop: "1rem" }}>
              Ozvěte se a společně ji <em>proměníme v realitu</em>
            </h2>
            <p>
              Vyplňte krátký formulář nebo nám rovnou zavolejte. Ozveme se
              do dvou pracovních dnů a domluvíme nezávaznou konzultaci —
              osobně i online.
            </p>
            <div className="cta-contacts">
              <div className="cta-contact">
                <span className="c-icon">
                  <PhoneIcon />
                </span>
                <div>
                  <div className="c-label">Telefon</div>
                  <div className="c-value">+420 774 287 783</div>
                  <div className="c-value" style={{ fontSize: "1rem", color: "rgba(239,231,216,0.7)" }}>+420 776 896 890</div>
                </div>
              </div>
              <div className="cta-contact">
                <span className="c-icon">
                  <MailIcon />
                </span>
                <div>
                  <div className="c-label">E-mail</div>
                  <div className="c-value">ahoj@tinyhousevolha.cz</div>
                </div>
              </div>
              <div className="cta-contact">
                <span className="c-icon">
                  <PinIcon />
                </span>
                <div>
                  <div className="c-label">Lokalita</div>
                  <div className="c-value">293 01 Mladá Boleslav</div>
                </div>
              </div>
            </div>
          </div>

          <form
            className="cta-form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = e.currentTarget as HTMLFormElement;
              f.reset();
              alert(
                "Děkujeme za poptávku. Ozveme se do dvou pracovních dnů."
              );
            }}
          >
            <h3>Nezávazná poptávka</h3>
            <p className="form-hint">
              Trvá to minutu. Nic nezavazuje.
            </p>

            <div className="form-row two">
              <div className="field">
                <label htmlFor="jmeno">Jméno</label>
                <input id="jmeno" name="jmeno" type="text" required placeholder="Vaše jméno" />
              </div>
              <div className="field">
                <label htmlFor="telefon">Telefon</label>
                <input id="telefon" name="telefon" type="tel" required placeholder="+420 ..." />
              </div>
            </div>

            <div className="field">
              <label htmlFor="email">E-mail</label>
              <input id="email" name="email" type="email" required placeholder="@" />
            </div>

            <div className="form-row two">
              <div className="field">
                <label htmlFor="typ">Typ projektu</label>
                <select id="typ" name="typ" defaultValue="tiny">
                  <option value="tiny">Tiny House na míru</option>
                  <option value="rekonstrukce">Rekonstrukce</option>
                  <option value="pristavba">Přístavba / dřevostavba</option>
                  <option value="terasa">Terasa / pergola</option>
                  <option value="jine">Jiné stavební práce</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="kdy">Plánovaný termín</label>
                <select id="kdy" name="kdy" defaultValue="pololeti">
                  <option value="dohromady">Do 3 měsíců</option>
                  <option value="pololeti">Do půl roku</option>
                  <option value="rok">Do roka</option>
                  <option value="pozdeji">Až později</option>
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="zprava">Vaše představa</label>
              <textarea
                id="zprava"
                name="zprava"
                placeholder="Krátký popis toho, co potřebujete — velikost, místo, rozpočet, cokoliv vás napadne."
              />
            </div>

            <button type="submit" className="btn btn-primary btn-arrow">
              Odeslat poptávku <ArrowRight />
            </button>
            <p className="form-disclaimer">
              Odesláním souhlasíte se zpracováním osobních údajů. Žádný spam,
              slibujeme. Viz <a href="#">zásady ochrany osobních údajů</a>.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */
function Footer() {
  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <a href="#top" className="brand">
              <span className="brand-mark">
                <img src="/brand/logo.jpg" alt="TinyHouse Volha" />
              </span>
              <span className="brand-text">
                <span className="brand-name">TinyHouse Volha</span>
                <span className="brand-sub">Výroba &amp; stavba</span>
              </span>
            </a>
            <p>
              Výroba Tiny House domů na míru a široké spektrum stavebních
              prací po celé České republice. Od roku 2018.
            </p>
          </div>

          <div className="footer-col">
            <h4>Služby</h4>
            <ul>
              <li><a href="#sluzby">Tiny House na míru</a></li>
              <li><a href="#sluzby">Hrubé stavby</a></li>
              <li><a href="#sluzby">Rekonstrukce</a></li>
              <li><a href="#sluzby">Bourací práce</a></li>
              <li><a href="#sluzby">Fasády a omítky</a></li>
              <li><a href="#sluzby">Obklady a dlažby</a></li>
              <li><a href="#sluzby">Podlahy</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>Firma</h4>
            <ul>
              <li><a href="#o-nas">O nás</a></li>
              <li><a href="#realizace">Realizace</a></li>
              <li><a href="#postup">Jak postupujeme</a></li>
              <li><a href="#kontakt">Kontakt</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>Kontakt</h4>
            <ul>
              <li><a href="tel:+420774287783">+420 774 287 783</a></li>
              <li><a href="tel:+420776896890">+420 776 896 890</a></li>
              <li>293 01 Mladá Boleslav</li>
              <li>Po–Pá 8:00–17:00</li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <div>
            © {new Date().getFullYear()} TinyHouse Volha. Všechna práva
            vyhrazena.
          </div>
          <div className="socials">
            <a href="#" aria-label="Facebook"><FacebookIcon /></a>
            <a href="#" aria-label="Instagram"><InstagramIcon /></a>
            <a href="#" aria-label="YouTube"><YoutubeIcon /></a>
          </div>
        </div>
      </div>
    </footer>
  );
}
